/**
 * TicketSheet — the printable A4 job check-in sheet (presentational only).
 *
 * Header is pre-filled from `data`; the inspection tables, notes, collection and
 * pricing fields print blank for hand-filling. The `variant` swaps the
 * Diagnostics/Service pricing region for a "billed on monthly invoice" banner.
 *
 * When `captured` is supplied (the device's check-in inspection was completed
 * digitally), the Reception check-in inspection region prints the captured
 * make/serial and each Yes/No check + notes instead of blank hand-fill rows.
 * The Technician inspection region always prints blank — it's a later
 * repair-bench checklist, not captured at reception.
 *
 * SM4RT-styled (design-system palette + section-header bars) rather than a pixel
 * match of the legacy sheet. Print/layout styling lives in TicketSheet.css.
 */
import React from 'react';
import type { CapturedInspection, TicketSheetData, TicketSheetVariant } from './ticketSheetData';
import { formatSheetTimestamp } from './ticketSheetData';
import {
  COLLECTION_METHODS,
  COLLECTION_NOTICE,
  DIAGNOSTIC_SERVICE_OPTIONS,
  DIAGNOSTIC_TYPE_OPTIONS,
  INVOICE_ID_PREFIX,
  MONTHLY_INVOICE_NOTICE,
  PASSWORDS_FOOTER,
  RECEPTION_DAMAGE_ROWS,
  RECEPTION_EXAMPLE_ROW,
  RECEPTION_STATUS_ROWS,
  SHEET_BRAND,
  SHEET_LOGO_SRC,
  TECHNICIAN_INSPECTION_ROWS,
  TP_CODES,
} from './ticketSheetContent';
import './TicketSheet.css';

export interface TicketSheetProps {
  data: TicketSheetData;
  variant: TicketSheetVariant;
  /** Completed check-in inspection for this device; fills the Reception region. */
  captured?: CapturedInspection;
}

const Box: React.FC<{ className?: string }> = ({ className }) => (
  <span className={`ts-check${className ? ` ${className}` : ''}`} aria-hidden />
);

const Fill: React.FC<{ prefix?: string; wide?: boolean; bigPrefix?: boolean }> = ({ prefix, wide, bigPrefix }) => (
  <span className={`ts-fill${wide ? ' ts-fill--wide' : ''}`}>
    {prefix != null && (
      <span className={`ts-fill__prefix${bigPrefix ? ' ts-fill__prefix--big' : ''}`}>{prefix}</span>
    )}
  </span>
);

const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
  <div className="ts-section__header">{title}</div>
);

/** A filled Yes/No/Notes row for a captured inspection check. */
const CapturedRow: React.FC<{ label: string; status: 'yes' | 'no' | null; notes: string | null }> = ({
  label,
  status,
  notes,
}) => (
  <tr>
    <td>{label}</td>
    <td className="ts-table__yn ts-table__tick">{status === 'yes' ? '✓' : ''}</td>
    <td className="ts-table__yn ts-table__tick">{status === 'no' ? '✓' : ''}</td>
    <td>{notes || ''}</td>
  </tr>
);

/**
 * Reception check-in inspection rendered from captured check-in data. Prints the
 * device identity and each Yes/No check + notes. Brand-new-in-box devices skip
 * the physical checks (the wizard does too), so we print the badge instead.
 */
const CapturedReception: React.FC<{ captured: CapturedInspection }> = ({ captured }) => {
  const completed = formatSheetTimestamp(captured.completedAt);
  const hasDamage = !captured.brandNewInBox && captured.damageRows.length > 0;
  const hasStatus = !captured.brandNewInBox && captured.itemStatusRows.length > 0;

  return (
    <>
      <div className="ts-capture-meta">
        <span className="ts-capture-meta__device">{captured.deviceLabel}</span>
        <span><strong>Make:</strong> {captured.manufacturer}</span>
        <span><strong>Serial:</strong> {captured.serial}</span>
        {captured.brandNewInBox && <span className="ts-capture-badge">Brand new — unopened box</span>}
        {captured.photoCount > 0 && (
          <span>{captured.photoCount} photo{captured.photoCount === 1 ? '' : 's'} on file</span>
        )}
      </div>

      {captured.brandNewInBox ? (
        <div className="ts-capture-note">
          Device received brand new in its unopened factory box — physical inspection skipped.
        </div>
      ) : (
        <table className="ts-table">
          <thead>
            <tr>
              <th className="ts-table__item">Item — damage</th>
              <th className="ts-table__yn">Yes</th>
              <th className="ts-table__yn">No</th>
              <th className="ts-table__notes">Notes</th>
            </tr>
          </thead>
          <tbody>
            {hasDamage &&
              captured.damageRows.map(row => (
                <CapturedRow key={row.key} label={row.label} status={row.status} notes={row.notes} />
              ))}
            {hasStatus && (
              <tr className="ts-table__subhead">
                <td colSpan={4}>Item — status</td>
              </tr>
            )}
            {hasStatus &&
              captured.itemStatusRows.map(row => (
                <CapturedRow key={row.key} label={row.label} status={row.status} notes={row.notes} />
              ))}
          </tbody>
        </table>
      )}

      {captured.otherNotes && (
        <div className="ts-capture-note">
          <strong>Other notes:</strong> {captured.otherNotes}
        </div>
      )}
      {completed && (
        <div className="ts-capture-foot">
          Captured {completed}
          {captured.completedBy ? ` by ${captured.completedBy}` : ''}
        </div>
      )}
    </>
  );
};

export const TicketSheet: React.FC<TicketSheetProps> = ({ data, variant, captured }) => {
  const isMonthly = variant === 'monthly';
  // Pre-printed prefix on the invoice-ID fields: the first 4 digits of the
  // current invoice sequence (e.g. "2611" → tech fills the last two), derived
  // from the latest invoice raised. Falls back to the year-only constant.
  const invoicePrefix = data.invoiceIdPrefix || INVOICE_ID_PREFIX;

  return (
    <div className="ts-sheet">
      {/* ── Header ── */}
      <header className="ts-sheet__header">
        {/* Left — date/time + customer information */}
        <div className="ts-sheet__hinfo">
          {data.dateTime && <span className="ts-sheet__hinfo-datetime">{data.dateTime}</span>}
          {data.contactName && <span>{data.contactName}</span>}
          {data.phone && <span>{data.phone}</span>}
          {data.email && <span>{data.email}</span>}
        </div>

        {/* Middle — ticket number over subject */}
        <div className="ts-sheet__hmain">
          <div className="ts-sheet__number">{data.ticketNumber || '—'}</div>
          <div className="ts-sheet__subject">{data.subject}</div>
        </div>

        {/* Right — logo (fixed width) */}
        <img className="ts-sheet__brand" src={SHEET_LOGO_SRC} alt={SHEET_BRAND} />
      </header>

      {/* ── Two-column body ── */}
      <div className="ts-sheet__cols">
        {/* Left column */}
        <div className="ts-sheet__col">
          <section className="ts-section">
            <SectionHeader title="Reception check-in inspection" />
            {captured ? (
              <CapturedReception captured={captured} />
            ) : (
              <table className="ts-table">
                <thead>
                  <tr>
                    <th className="ts-table__item">Item — damage</th>
                    <th className="ts-table__yn">Yes</th>
                    <th className="ts-table__yn">No</th>
                    <th className="ts-table__notes">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="ts-table__example">
                    <td>{RECEPTION_EXAMPLE_ROW.item}</td>
                    <td className="ts-table__yn ts-table__tick">✓</td>
                    <td className="ts-table__yn" />
                    <td>{RECEPTION_EXAMPLE_ROW.notes}</td>
                  </tr>
                  {RECEPTION_DAMAGE_ROWS.map((row) => (
                    <tr key={row}>
                      <td>{row}</td>
                      <td className="ts-table__yn" />
                      <td className="ts-table__yn" />
                      <td />
                    </tr>
                  ))}
                  <tr className="ts-table__subhead">
                    <td colSpan={4}>Item — status</td>
                  </tr>
                  {RECEPTION_STATUS_ROWS.map((row) => (
                    <tr key={row}>
                      <td>{row}</td>
                      <td className="ts-table__yn" />
                      <td className="ts-table__yn" />
                      <td />
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          <section className="ts-section">
            <SectionHeader title="Technician inspection" />
            <table className="ts-table">
              <thead>
                <tr>
                  <th className="ts-table__item">Task</th>
                  <th className="ts-table__yn">Pre</th>
                  <th className="ts-table__yn">Post</th>
                  <th className="ts-table__notes">Notes</th>
                </tr>
              </thead>
              <tbody>
                {TECHNICIAN_INSPECTION_ROWS.map((row) => (
                  <tr key={row}>
                    <td>{row}</td>
                    <td className="ts-table__yn" />
                    <td className="ts-table__yn" />
                    <td />
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          {/* TP codes — hidden for brand-new-in-box devices (nothing to mark). */}
          {!captured?.brandNewInBox && (
            <section className="ts-section ts-tp">
              <SectionHeader title="TP" />
              <div className="ts-tp__codes">
                {TP_CODES.map((code) => (
                  <span key={code} className="ts-tp__code">{code}</span>
                ))}
              </div>
            </section>
          )}
        </div>

        {/* Right column */}
        <div className="ts-sheet__col">
          <section className="ts-section">
            <SectionHeader title="Check In Charges" />
            {isMonthly ? (
              <div className="ts-monthly-notice">{MONTHLY_INVOICE_NOTICE}</div>
            ) : (
              <div className="ts-box ts-box--diag">
                <div className="ts-diag-grid">
                  <div>
                    <div className="ts-diag-grid__head">Type</div>
                    {DIAGNOSTIC_TYPE_OPTIONS.map((o) => (
                      <div key={o.label} className="ts-option">
                        <span>{o.label} <em>{o.price}</em></span>
                        <Box />
                      </div>
                    ))}
                  </div>
                  <div>
                    <div className="ts-diag-grid__head">Service</div>
                    {DIAGNOSTIC_SERVICE_OPTIONS.map((o) => (
                      <div key={o.label} className="ts-option">
                        <span>{o.label} <em>{o.price}</em></span>
                        <Box />
                      </div>
                    ))}
                  </div>
                </div>
                <div className="ts-field"><span>Diag invoice ID</span><Fill prefix={invoicePrefix} wide bigPrefix /></div>
                <div className="ts-field ts-field--tall"><span>Diag total</span><Fill prefix="$" wide /></div>
                <div className="ts-field ts-field--inline ts-field--right ts-field--short"><span>Paid</span><Box /></div>
              </div>
            )}
          </section>

          <section className="ts-section">
            <SectionHeader title="Service Charges" />
            {isMonthly ? (
              <div className="ts-monthly-notice">{MONTHLY_INVOICE_NOTICE}</div>
            ) : (
              <div className="ts-box">
                <div className="ts-field"><span>Service invoice ID</span><Fill prefix={invoicePrefix} wide bigPrefix /></div>
                <div className="ts-field ts-field--tall"><span>Service total</span><Fill prefix="$" wide /></div>
                <div className="ts-field ts-field--inline ts-field--right ts-field--short">
                  <span>GR</span><Box />
                  <span className="ts-field__gap ts-field__paid">PAID</span><Box />
                </div>
                <div className="ts-field ts-field--tall"><span>Outstanding</span><Fill prefix="$" wide /></div>
              </div>
            )}
          </section>

          <section className="ts-section">
            <SectionHeader title="Collection" />
            <div className="ts-box ts-collection">
              <p className="ts-collection__notice">{COLLECTION_NOTICE}</p>
              <div className="ts-collection__row">
                <span className="ts-collection__methods">{COLLECTION_METHODS.join(' / ')}</span>
                <span className="ts-collection__when"><Fill /> : <Fill /></span>
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* ── Notes ── */}
      <section className="ts-section ts-notes">
        <SectionHeader title="Notes" />
        <div className="ts-notes__lines">
          {Array.from({ length: 10 }).map((_, i) => (
            <div key={i} className="ts-notes__line" />
          ))}
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="ts-sheet__footer">
        <div className="ts-sheet__footer-title">
          TICKET NO. {data.ticketNumber || '—'} {PASSWORDS_FOOTER}
        </div>
        <div className="ts-sheet__footer-line" />
        <div className="ts-sheet__footer-line" />
      </footer>
    </div>
  );
};
